import React, { useEffect, useMemo } from 'react';
import { useLocation, NavLink } from 'react-router-dom';
import PropTypes from 'prop-types';

import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Icon from '@mui/material/Icon';
import Tooltip from '@mui/material/Tooltip';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import SidenavCollapse from 'examples/Sidenav/SidenavCollapse';
import SidenavRoot from 'examples/Sidenav/SidenavRoot';
import sidenavLogoLabel from 'examples/Sidenav/styles/sidenav';
import { buildPilatesSidebarRoutes } from 'sectors/pilates/nav/buildPilatesSidebarRoutes';

import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from 'context';

// ✅ relative import (alias yok)
import { TrialUpgradeCard } from '../../sectors/pilates/nav/TrialUpgradeCard';
import { logout } from 'core/auth/logout';
import MDButton from 'components/MDButton';
import { alpha } from '@mui/material/styles';

function Sidenav({ color = 'info', brand = '', meta = null, brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;

  const location = useLocation();
  const collapseName = useMemo(() => {
    const parts = (location.pathname || '').split('/').filter(Boolean);
    // /app/pilates/members  => ["app","pilates","members"]
    return parts[2] || 'dash'; // 2 = "members"
  }, [location.pathname]);

  const resolvedMeta = meta && typeof meta === 'object' ? meta.meta ?? meta : null;

  const sector = resolvedMeta?.sector || resolvedMeta?.sectorKey || null; // "PILATES"
  const plan = resolvedMeta?.plan || 'TRIAL';
  const perms = resolvedMeta?.perms || {};
  const [openGroups, setOpenGroups] = React.useState({});
  const toggleGroup = (k) => setOpenGroups((s) => ({ ...s, [k]: !s[k] }));

  // ✅ tek kaynak: effectiveRoutes
  const effectiveRoutes = useMemo(() => {
    if (sector === 'PILATES') return buildPilatesSidebarRoutes({ plan, perms });
    return routes;
  }, [sector, plan, perms, routes]);

  // text color (orijinal davranış)
  let textColor = 'white';
  if (transparentSidenav || (whiteSidenav && !darkMode)) textColor = 'dark';
  else if (whiteSidenav && darkMode) textColor = 'inherit';

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    window.addEventListener('resize', handleMiniSidenav);
    handleMiniSidenav();

    return () => window.removeEventListener('resize', handleMiniSidenav);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, location.pathname]);

  const pathname = location.pathname || '';

  const isActiveRoute = (r) => {
    if (!r?.route) return false;
    return pathname === r.route || pathname.startsWith(`${r.route}/`);
  };

  const renderRouteItem = (r, depth = 0) => {
    const {
      type,
      name,
      icon,
      title,
      noCollapse,
      key,
      href,
      route,
      locked,
      lockedReason,
      collapse,
    } = r;

    if (type === 'collapse') {
      const hasChildren = Array.isArray(collapse) && collapse.length > 0;

      const pl = depth ? 3 + depth * 1.5 : 3;

      // dış link
      if (href) {
        return (
          <Link
            key={key}
            href={href}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: 'none' }}
          >
            <SidenavCollapse name={name} icon={icon} active={false} sx={{ pl }} />
          </Link>
        );
      }

      // kilitli
      if (locked) {
        return (
          <Tooltip
            key={key}
            title={lockedReason || 'Bu özellik plan/izin gerektiriyor'}
            placement="right"
          >
            <span>
              <SidenavCollapse name={name} icon={icon} active={false} locked sx={{ pl }} />
            </span>
          </Tooltip>
        );
      }

      // ✅ parent (submenu’lü)
      if (hasChildren) {
        const parentActive = isActiveRoute(r) || collapse.some((c) => isActiveRoute(c));
        const isOpen = Boolean(openGroups[key] ?? parentActive);

        return (
          <React.Fragment key={key}>
            {/* parent row */}
            <MDBox
              onClick={() => toggleGroup(key)}
              sx={{
                cursor: 'pointer',
                userSelect: 'none',
                WebkitTapHighlightColor: 'transparent',
                position: 'relative',
              }}
            >
              <SidenavCollapse
                name={name}
                icon={icon} // ✅ sadece ikon
                active={parentActive}
                noCollapse
                sx={{ pl, pr: miniSidenav ? 0 : 4 }} // sağda chevron için boşluk
              />

              {/* chevron (sağda) */}
              {!miniSidenav && (
                <MDBox
                  sx={{
                    position: 'absolute',
                    right: 14,
                    top: '50%',
                    transform: `translateY(-50%) rotate(${isOpen ? 180 : 0}deg)`,
                    opacity: 0.7,
                    transition: 'transform 160ms ease',
                    pointerEvents: 'none',
                  }}
                >
                  <Icon sx={{ fontSize: 18 }}>expand_more</Icon>
                </MDBox>
              )}
            </MDBox>

            {/* children */}
            {isOpen ? (
              <MDBox
                sx={{
                  pl: 1,
                  ml: depth ? 1 : 0.5,
                  borderLeft: (t) => `2px solid ${alpha(t.palette.divider, 0.4)}`,
                }}
              >
                {collapse.map((c) => renderRouteItem({ ...c, type: 'collapse' }, depth + 1))}
              </MDBox>
            ) : null}
          </React.Fragment>
        );
      }

      // leaf route
      const active = isActiveRoute(r);

      return (
        <NavLink
          key={key}
          to={route}
          style={{ textDecoration: 'none', display: 'block' }}
          onClick={() => {
            if (window.innerWidth < 1200) closeSidenav();
          }}
        >
          <MDBox sx={{ py: 0.15 }}>
            <SidenavCollapse
              name={name}
              icon={icon}
              active={active}
              noCollapse={noCollapse}
              sx={{ pl }}
            />
          </MDBox>
        </NavLink>
      );
    }

    if (type === 'title') {
      return (
        <MDTypography
          key={key}
          color={textColor}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </MDTypography>
      );
    }

    if (type === 'divider') {
      return (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    }

    return null;
  };

  const renderRoutes = effectiveRoutes.map((r) => renderRouteItem(r));

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      {/* Header */}
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        {/* close (mobil) */}
        <MDBox
          display={{ xs: 'block', xl: 'none' }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: 'pointer' }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: 'bold' }}>close</Icon>
          </MDTypography>
        </MDBox>

        <MDBox
          component={NavLink}
          to="/"
          display="flex"
          alignItems="center"
          sx={{ textDecoration: 'none' }}
        >
          {brand && <MDBox component="img" src={brand} alt="Brand" width="2rem" />}
          <MDBox
            width={!brandName && '100%'}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}>
              {brandName}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>

      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />

      {/* ✅ TEK LİSTE */}
      <List>{renderRoutes}</List>

      {/* ✅ Trial kart (sadece pilates trial) */}
      {sector === 'PILATES' && plan === 'TRIAL' ? (
        <MDBox p={2} mt="auto">
          <TrialUpgradeCard onUpgrade={() => console.log('OPEN_UPGRADE')} />
        </MDBox>
      ) : (
        <MDBox mt="auto" />
      )}
      {/* ✅ FOOTER */}
      <MDBox mt="auto" p={2}>
        <MDButton
          fullWidth
          variant="outlined"
          color="error"
          onClick={logout}
          startIcon={<Icon>logout</Icon>}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          Çıkış Yap
        </MDButton>
      </MDBox>
    </SidenavRoot>
  );
}

Sidenav.propTypes = {
  color: PropTypes.oneOf(['primary', 'secondary', 'info', 'success', 'warning', 'error', 'dark']),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  meta: PropTypes.shape({
    sector: PropTypes.string,
    plan: PropTypes.oneOf(['TRIAL', 'BASIC', 'PRO', 'ENTERPRISE']),
    perms: PropTypes.object,
  }),
};

export default Sidenav;
